import { useEffect, useMemo, useState } from 'react';
import { shiftAPI, getApiErrorMessage } from '@matix/api-client';
import type { ShiftSummaryDto } from '@matix/api-client';
import { DENOMINATIONS, useShift } from '../../store/shift';
import { useUI } from '../../store/ui';
import { useOutbox } from '../../store/outbox';
import { fmt } from '../../lib/mock-data';
import { toast } from '../../store/toast';
import { useLang } from '../../lib/language';
import { CheckIcon, XIcon } from '../../components/icons';
import { ShiftReceiptDrillDown } from './ShiftReceiptDrillDown';
import { CloseShiftDialog } from './CloseShiftDialog';
import { useModuleOn } from '../../store/tenantConfig';

/**
 * Shift-close screen — fullscreen takeover entered via the user dropdown.
 *
 * The screen itself is a review surface: the shift's invoices get the room,
 * with a compact drawer rail beside them. Cash entry lives in
 * CloseShiftDialog, opened by "Close shift", so nothing on this screen asks
 * the cashier to count before they have looked at what they sold.
 *
 * Drill-down (Phase 10.9) is a child component opened from the receipts
 * tabs. It also renders the PaymentMethodEditDialog inline for changes.
 */
export function ShiftCloseScreen() {
  const { closeMode, closeCounts, closeNotes, closeTotal, resetClose } = useShift();
  const setScreen = useUI((s) => s.setScreen);
  const showModal = useUI((s) => s.showModal);
  const { t } = useLang();

  const [summary, setSummary] = useState<ShiftSummaryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [zReportId, setZReportId] = useState<number | null>(null);
  // The card / network count (migration 071). Offered on a LAUNDRY tenant —
  // the only one that asked for it — so every other till closes as before.
  const laundryOn = useModuleOn('laundry');
  const [tenderTexts, setTenderTexts] = useState<Record<number, string>>({});
  const countTenders = laundryOn && summary?.tender_count?.available ? summary.tender_count.tenders : [];

  // ── Load summary on mount ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    shiftAPI.summary()
      .then((s) => { if (!cancelled) setSummary(s); })
      .catch((err) => toast(getApiErrorMessage(err), 'error'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const total = closeTotal();

  const openedAtStr = useMemo(() => {
    if (!summary) return '—';
    // Backend ships opened_at as a naive tenant-TZ string; parse components
    // verbatim so the displayed open time matches the shift's wall-clock
    // record regardless of host timezone.
    const naive = /^\d{4}-\d{2}-\d{2}[T ](\d{2}):(\d{2})/.exec(
      String(summary.shift.opened_at).trim(),
    );
    let hour24: number; let minute: number;
    if (naive) {
      hour24 = Number(naive[1]); minute = Number(naive[2]);
    } else {
      const d = new Date(summary.shift.opened_at);
      hour24 = d.getHours(); minute = d.getMinutes();
    }
    const suffix = hour24 >= 12 ? 'PM' : 'AM';
    const h12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    return `${String(h12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${suffix}`;
  }, [summary]);

  const elapsedStr = useMemo(() => {
    if (!summary) return '—';
    const ms = Date.now() - new Date(summary.shift.opened_at).getTime();
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}m`;
  }, [summary]);

  async function handleConfirm() {
    if (!summary) return;
    // Same rule the sidebar enforces, repeated here because this is the step
    // that actually writes the shift: the server's expected cash counts only
    // posted receipts, so closing over a queued sale books a variance that is
    // pure artefact. See Sidebar.startCloseShift.
    if (useOutbox.getState().pendingCount() + useOutbox.getState().failedCount() > 0) {
      toast(
        t({
          en: 'There are offline sales that have not been saved yet. Sync them before closing.',
          ar: 'توجد مبيعات دون اتصال لم تُحفظ بعد. زامنها قبل الإغلاق.',
        }),
        'error',
      );
      return;
    }
    // A zero count is a real close — a shift with no cash sales, or a drawer
    // already emptied — so only a negative is refused (the input clamps at 0
    // anyway). The server accepts counted_total >= 0.
    if (total < 0) {
      toast(t({ en: 'Counted total cannot be negative', ar: 'لا يمكن أن يكون الإجمالي المعدود سالبًا' }), 'error');
      return;
    }
    // Only a by-denomination count has a breakdown to send; a typed total
    // goes up as count_mode 'single' so the server records no denominations
    // rather than a fabricated "total × 1" row.
    const denominations = closeMode === 'denom'
      ? DENOMINATIONS
          .map((d) => ({ denomination: d.value, count: closeCounts[String(d.value)] ?? 0 }))
          .filter((d) => d.count > 0)
      : [];

    if (closeMode === 'denom' && denominations.length === 0) {
      toast(t({ en: 'Enter at least one denomination', ar: 'أدخل فئة واحدة على الأقل' }), 'error');
      return;
    }

    setSubmitting(true);
    try {
      // A card left blank is "not counted" and sends nothing.
      const tender_counts = countTenders
        .filter((x) => (tenderTexts[x.payment_type_id] ?? '').trim() !== '')
        .map((x) => ({ payment_type_id: x.payment_type_id, counted: Math.max(0, parseFloat(tenderTexts[x.payment_type_id]!) || 0) }));
      const res = await shiftAPI.close({
        count_mode: closeMode,
        denominations,
        counted_total: total,
        notes: closeNotes || undefined,
        ...(tender_counts.length > 0 ? { tender_counts } : {}),
      });
      setDialogOpen(false);
      setZReportId(res.z_report_id);
      toast(
        t({
          en: `Shift closed · variance ${fmt(res.variance)} · generating Z-Report…`,
          ar: `تم إغلاق الوردية · الفرق ${fmt(res.variance)} · جارٍ إنشاء تقرير Z…`,
        }),
        'success',
      );
      resetClose();
      // Hand off to the Z-Report modal which fetches by z_report_id.
      // We pass via window state since the modal store is intentionally
      // typed loose for transient ids — kept consistent with how
      // other transient ids flow today.
      (window as unknown as { __pos_zreport_shift_id?: number }).__pos_zreport_shift_id = res.shift_id;
      showModal('zreport');
    } catch (err) {
      toast(getApiErrorMessage(err), 'error');
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="fullscreen" style={{ alignItems: 'center', justifyContent: 'center', display: 'flex', color: 'var(--text-muted)' }}>
        {t({ en: 'Loading shift summary…', ar: 'جارٍ تحميل ملخص الوردية…' })}
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="fullscreen" style={{ padding: 40 }}>
        <header className="topbar">
          <div className="topbar-title">{t({ en: 'Close shift', ar: 'إغلاق الوردية' })}</div>
          <div className="topbar-spacer" />
          <button className="icon-btn" onClick={() => setScreen('app')} title={t({ en: 'Cancel', ar: 'إلغاء' })}>
            <XIcon size={20} />
          </button>
        </header>
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
          {t({ en: 'No open shift to close. Make a sale or record a movement to open one.', ar: 'لا توجد وردية مفتوحة لإغلاقها. قم بعملية بيع أو سجّل حركة لفتح واحدة.' })}
        </div>
      </div>
    );
  }

  const eb = summary.expected_breakdown;
  const pt = summary.payment_totals;
  const ss = summary.sales_summary;

  return (
    <div className="fullscreen" id="shiftCloseScreen">
      <header className="topbar">
        <div className="topbar-title">{t({ en: 'Close shift', ar: 'إغلاق الوردية' })}</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginLeft: 16 }}>
          {t({ en: `Shift opened at ${openedAtStr} · Open for ${elapsedStr}`, ar: `فُتحت الوردية في ${openedAtStr} · مفتوحة منذ ${elapsedStr}` })}
        </div>
        <div className="topbar-spacer" />
        <button
          className="icon-btn"
          onClick={() => setScreen('app')}
          title={t({ en: 'Cancel', ar: 'إلغاء' })}
          disabled={submitting}
        >
          <XIcon size={20} />
        </button>
      </header>

      <div className="shift-screen">
        {/* The invoices list is the point of this screen — it gets the room. */}
        <div className="shift-main">
          <ShiftReceiptDrillDown summary={summary} />
        </div>

        <div className="shift-side">
          <div className="shift-summary-title">{t({ en: 'Sales summary', ar: 'ملخص المبيعات' })}</div>
          <SalesSummaryCard ss={ss} />

          <div className="shift-summary-title" style={{ marginTop: 16 }}>
            {t({ en: 'Payment totals', ar: 'إجماليات الدفع' })}
          </div>
          <PaymentTotalsCard pt={pt} />

          <div className="shift-drawer-strip">
            <div className="sds-row">
              <span>{t({ en: 'Cash sales', ar: 'المبيعات النقدية' })}</span>
              <span>{fmt(eb.cash_sales)}</span>
            </div>
            <div className="sds-row">
              <span>{t({ en: 'Cash returns', ar: 'المرتجعات النقدية' })}</span>
              <span className="neg">{eb.cash_refunds === 0 ? fmt(0) : `-${fmt(eb.cash_refunds)}`}</span>
            </div>
            {/* Cash expense vouchers paid out of this drawer (Expenses screen) —
                only shown when there are any, so every other shift reads as before. */}
            {(eb.expense_vouchers_cash ?? 0) > 0 && (
              <div className="sds-row">
                <span>{t({ en: 'Cash expenses', ar: 'مصروفات نقدية' })}</span>
                <span className="neg">-{fmt(eb.expense_vouchers_cash ?? 0)}</span>
              </div>
            )}
            <div className="sds-row total">
              <span>{t({ en: 'Expected cash', ar: 'النقد المتوقع' })}</span>
              <span>{fmt(eb.expected_total)}</span>
            </div>
          </div>

          <div className="shift-summary-title" style={{ marginTop: 16 }}>
            {t({ en: 'Movements', ar: 'الحركات' })}
          </div>
          <MovementsCard summary={summary} />

          {/* .btn-shift carries margin-top:auto — it pins to the rail's foot. */}
          <button
            className="btn-shift"
            onClick={() => setDialogOpen(true)}
            disabled={submitting}
          >
            {t({ en: 'Close shift', ar: 'إغلاق الوردية' })}
            <CheckIcon size={18} />
          </button>

          {zReportId != null && (
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
              {t({ en: `Z-Report id #${zReportId} generated.`, ar: `تم إنشاء تقرير Z رقم #${zReportId}.` })}
            </div>
          )}
        </div>
      </div>

      {dialogOpen && (
        <CloseShiftDialog
          summary={summary}
          submitting={submitting}
          onCancel={() => setDialogOpen(false)}
          onConfirm={handleConfirm}
          tenders={countTenders}
          tenderTexts={tenderTexts}
          onTenderText={(id, text) => setTenderTexts((m) => ({ ...m, [id]: text }))}
        />
      )}
    </div>
  );
}

/**
 * Revenue side of the shift — the numbers the cashier is asked about at
 * handover ("how much did you discount today?"), which the cash-only strip
 * below could never answer.
 *
 * Laid out so it reads as the arithmetic it is: sales less returns give
 * total sales, then discount comes off and tax goes on to reach net sales.
 * Delivery fees appear only when the tenant charges them — an always-zero
 * row would just be noise on a restaurant that doesn't deliver.
 */
function SalesSummaryCard({ ss }: { ss: ShiftSummaryDto['sales_summary'] }) {
  const { t } = useLang();
  return (
    <div className="shift-drawer-strip" style={{ marginTop: 0 }}>
      <div className="sds-row">
        <span>{t({ en: 'Sales', ar: 'المبيعات' })}</span>
        <span>{fmt(ss.sales)}</span>
      </div>
      <div className="sds-row">
        <span>{t({ en: 'Returns', ar: 'المرتجعات' })}</span>
        <span className="neg">{ss.returns === 0 ? fmt(0) : `-${fmt(ss.returns)}`}</span>
      </div>
      <div className="sds-row sub">
        <span>{t({ en: 'Total sales', ar: 'إجمالي المبيعات' })}</span>
        <span>{fmt(ss.total_sales)}</span>
      </div>
      <div className="sds-row">
        <span>{t({ en: 'Discount', ar: 'الخصم' })}</span>
        <span className="neg">{ss.discounts === 0 ? fmt(0) : `-${fmt(ss.discounts)}`}</span>
      </div>
      <div className="sds-row">
        <span>{t({ en: 'Tax', ar: 'الضريبة' })}</span>
        <span>{fmt(ss.tax)}</span>
      </div>
      {ss.delivery_fees !== 0 && (
        <div className="sds-row">
          <span>{t({ en: 'Delivery fees', ar: 'رسوم التوصيل' })}</span>
          <span>{fmt(ss.delivery_fees)}</span>
        </div>
      )}
      <div className="sds-row total">
        <span>{t({ en: 'Net sales', ar: 'صافي المبيعات' })}</span>
        <span>{fmt(ss.net_sales)}</span>
      </div>
    </div>
  );
}

function PaymentTotalsCard({ pt }: { pt: ShiftSummaryDto['payment_totals'] }) {
  const { t } = useLang();
  const rows: Array<[string, string, { count: number; total: number }]> = [
    ['Cash', t({ en: 'Cash', ar: 'نقدي' }), pt.cash],
    ['Card', t({ en: 'Card', ar: 'بطاقة' }), pt.card],
    ['Wallet', t({ en: 'Wallet', ar: 'محفظة' }), pt.wallet],
    ['Credit', t({ en: 'Credit', ar: 'آجل' }), pt.credit],
    ['Other', t({ en: 'Other', ar: 'أخرى' }), pt.other],
  ];
  return (
    <div className="payment-totals-card" style={{ display: 'grid', gap: 4 }}>
      {rows.map(([key, label, b]) => (
        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
          <span style={{ color: 'var(--text-muted)' }}>{label} ({b.count})</span>
          <span>{fmt(b.total)}</span>
        </div>
      ))}
    </div>
  );
}

function MovementsCard({ summary }: { summary: ShiftSummaryDto }) {
  const { t } = useLang();
  if (summary.movements.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        {t({ en: 'No movements this shift.', ar: 'لا توجد حركات في هذه الوردية.' })}
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 4, fontSize: 12 }}>
      {summary.movements.slice(0, 6).map((m) => (
        <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--text-muted)' }}>
            {m.movement_type} · {m.reason ?? '—'}
          </span>
          <span>{fmt(m.amount)}</span>
        </div>
      ))}
      {summary.movements.length > 6 && (
        <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
          {t({ en: `+ ${summary.movements.length - 6} more`, ar: `+ ${summary.movements.length - 6} المزيد` })}
        </div>
      )}
    </div>
  );
}
